import Echo from 'laravel-echo';
import { store } from '../store';
import {
  ActiveUser,
  addActiveUser,
  clearActiveUsers,
  removeActiveUser,
  setActiveUsers,
} from '../store/siteInfoSlice';
import { getEcho, initializeRealtime } from './echo-client';

type PresenceChannel = ReturnType<Echo<'reverb'>['join']>;

class PresenceService {
  private channels = new Map<string, PresenceChannel>();
  private subscribers = new Map<string, Set<string>>();
  private connectionBound = false;
  private reauthThrottleMs = 1500;
  private lastReauthAt = 0;

  async ensureEcho(): Promise<Echo<'reverb'>> {
    const existing = getEcho();
    if (existing) return existing;
    return initializeRealtime();
  }

  register(channelName: string, subscriberId: string): PresenceChannel | null {
    if (!channelName) return null;
    if (!this.subscribers.has(channelName))
      this.subscribers.set(channelName, new Set());
    this.subscribers.get(channelName)!.add(subscriberId);

    const echo = getEcho();
    if (!echo) return null;

    let channel = this.channels.get(channelName);
    if (!channel) {
      try {
        channel = echo.join(channelName);
        this.channels.set(channelName, channel);
        this.attachPresenceHandlers(channel, channelName);
        this.bindConnectionHandlersOnce();
      } catch (_e) {
        return null;
      }
    }
    return channel;
  }

  unregister(channelName: string, subscriberId: string): void {
    const subs = this.subscribers.get(channelName);
    if (!subs) return;
    subs.delete(subscriberId);
    if (subs.size === 0) this.leave(channelName);
  }

  leave(channelName: string): void {
    const echo = getEcho();
    const ch = this.channels.get(channelName);
    if (!echo || !ch) return;
    try {
      echo.leave(channelName);
    } catch (_e) {
      /* noop */
    }
    this.channels.delete(channelName);
    this.subscribers.delete(channelName);
  }

  leaveAll(): void {
    const echo = getEcho();
    for (const name of this.channels.keys()) {
      try {
        echo?.leave(name);
      } catch (_e) {
        /* noop */
      }
    }
    this.channels.clear();
    this.subscribers.clear();
    store.dispatch(clearActiveUsers());
  }

  forceReauthorizeAll(): void {
    const echo = getEcho();
    if (!echo) return;
    for (const name of this.channels.keys()) {
      try {
        echo.leave(name);
        const ch = echo.join(name);
        this.channels.set(name, ch);
        this.attachPresenceHandlers(ch, name);
      } catch (_e) {
        /* noop */
      }
    }
  }

  private attachPresenceHandlers(channel: PresenceChannel, _name: string) {
    channel
      .here((users: ActiveUser[]) => {
        const record: Record<string, ActiveUser> = {};
        users.forEach((u) => (record[u.id] = u));
        store.dispatch(setActiveUsers(record));
      })
      .joining((user: ActiveUser) => {
        store.dispatch(addActiveUser(user));
      })
      .leaving((user: ActiveUser) => {
        store.dispatch(removeActiveUser(user.id));
      })
      .error((_err: Error) => {
        console.error('Presence error', _err);
      });
  }

  private bindConnectionHandlersOnce(): void {
    if (this.connectionBound) return;
    this.connectionBound = true;
    const echo = getEcho() as unknown as {
      connector?: {
        pusher?: {
          connection?: { bind: (ev: string, cb: () => void) => void };
        };
      };
    } | null;
    const connection = echo?.connector?.pusher?.connection;
    if (!connection) return;
    const throttledReauth = () => {
      const now = Date.now();
      if (now - this.lastReauthAt < this.reauthThrottleMs) return;
      this.lastReauthAt = now;
      this.forceReauthorizeAll();
    };
    // Only on reconnection – avoid double-join on initial connect
    connection.bind('reconnected', throttledReauth);
  }
}

export const presenceService = new PresenceService();
